// src/config/examConfigs.ts
// Centrale configuratie voor alle WFT examens
// Gebruikt voor SEO pagina's, structured data en GEO optimalisatie
// UPDATED: Uitgebreide GEO-geoptimaliseerde FAQs + WFT Leven verwijderd

export interface ExamConfig {
  id: string;
  slug: string;
  name: string;
  fullName: string;
  shortDescription: string;
  longDescription: string;
  
  // Examen specificaties (officiële CDFD data)
  specs: {
    totalQuestions: number;
    duration: number; // in minuten
    passingScore: number; // percentage
    passingPoints: number;
    maxPoints: number;
    kbQuestions: number;
    kbPoints: number;
    vcQuestions: number;
    vcPoints: number;
    pgQuestions: number;
    pgPoints: number;
  };
  
  // Kosten
  pricing: {
    examCost: number; // CDFD examenkosten
    retakeCost: number;
  };
  
  // Hoofdonderwerpen voor SEO
  topics: string[];
  
  // Keywords voor SEO
  keywords: string[];
  
  // FAQ items voor GEO
  faqs: {
    question: string;
    answer: string;
  }[];
  
  // Meta informatie
  meta: {
    title: string;
    description: string;
    ogImage: string;
  };
  
  // Status
  isActive: boolean;
  hasQuestions: boolean;
  hasSummaries: boolean;
}

export const examConfigs: Record<string, ExamConfig> = {
  'schade-particulier': {
    id: 'schade-particulier',
    slug: 'wft-schade-particulier',
    name: 'Schade Particulier',
    fullName: 'WFT Schade Particulier',
    shortDescription: 'Verzekeringen voor particulieren: inboedel, opstal, aansprakelijkheid en meer.',
    longDescription: 'Het WFT Schade Particulier examen toetst je kennis over schadeverzekeringen voor particulieren. Dit omvat inboedel- en opstalverzekeringen, aansprakelijkheidsverzekeringen (AVP), rechtsbijstandverzekeringen, reis- en annuleringsverzekeringen, en motorrijtuigverzekeringen. Je leert klanten adviseren over de juiste dekking en polisvoorwaarden.',
    
    specs: {
      totalQuestions: 48,
      duration: 120,
      passingScore: 68,
      passingPoints: 49,
      maxPoints: 71,
      kbQuestions: 25,
      kbPoints: 25,
      vcQuestions: 22,
      vcPoints: 44,
      pgQuestions: 1,
      pgPoints: 2
    },
    
    pricing: {
      examCost: 175,
      retakeCost: 175
    },
    
    topics: [
      'Inboedelverzekering',
      'Opstalverzekering',
      'Aansprakelijkheidsverzekering Particulieren (AVP)',
      'Rechtsbijstandverzekering',
      'Reisverzekering',
      'Annuleringsverzekering',
      'Motorrijtuigverzekering (WAM)',
      'Cascoverzekering',
      'Bromfietsverzekering',
      'Caravanverzekering',
      'Pleziervaartuigenverzekering',
      'Kostbaarhedenverzekering',
      'Glasverzekering'
    ],
    
    keywords: [
      'wft schade particulier',
      'wft schade particulier oefenvragen',
      'wft schade particulier examen',
      'wft schade particulier samenvatting',
      'schade particulier oefenen',
      'wft particulier verzekeringen',
      'cdfd schade particulier'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Schade Particulier examen?',
        answer: 'Het WFT Schade Particulier examen bestaat uit 48 vragen verdeeld over drie categorieën. Er zijn 25 Kennis & Begrip (KB) vragen die elk 1 punt opleveren, 22 Vaardigheden & Competenties (VC) vragen die elk 2 punten opleveren, en 1 Professioneel Gedrag (PG) vraag die ook 2 punten waard is. Het maximaal te behalen puntenaantal is 71. Om te slagen heb je minimaal 68% van de punten nodig, dus 49 punten. Het grootste deel van de punten zit in de VC-vragen met casussen. Je hebt 120 minuten (2 uur) de tijd om alle vragen te beantwoorden. Deze samenstelling geldt voor examens vanaf 1 april 2026.'
      },
      {
        question: 'Wat is de slaaggrens voor WFT Schade Particulier?',
        answer: 'De slaaggrens voor het WFT Schade Particulier examen is 68%. Dit betekent dat je minimaal 49 van de 71 punten moet behalen om te slagen. Bij precies 68% krijg je een 5,5 dat wordt afgerond naar een 6, wat een voldoende is. Het is belangrijk om te weten dat de verschillende vraagtypen verschillende puntenwaarden hebben: KB-vragen leveren 1 punt op, terwijl VC- en PG-vragen elk 2 punten waard zijn. Een strategische aanpak is om extra aandacht te besteden aan de VC- en PG-vragen, omdat fouten daar zwaarder wegen.'
      },
      {
        question: 'Welke onderwerpen komen aan bod bij het WFT Schade Particulier examen?',
        answer: 'Het WFT Schade Particulier examen behandelt alle schadeverzekeringen voor particulieren. De hoofdonderwerpen zijn: inboedelverzekeringen (dekking, onder- en oververzekering, clausules), opstalverzekeringen (herbouwwaarde, fundamentele gebreken), de Aansprakelijkheidsverzekering Particulieren (AVP) met wettelijke aansprakelijkheid, motorrijtuigverzekeringen (WAM, casco, bonus-malus), rechtsbijstandverzekeringen, reis- en annuleringsverzekeringen, en de basiskennis van zorgverzekeringen. Je moet ook de wettelijke kaders kennen zoals het Burgerlijk Wetboek, polisvoorwaarden kunnen interpreteren, en klanten kunnen adviseren over de juiste dekking. Daarnaast worden praktische vaardigheden getoetst zoals het opstellen van een risicoprofiel en het afhandelen van schademeldingen.'
      },
      {
        question: 'Hoe lang moet ik studeren voor WFT Schade Particulier?',
        answer: 'De gemiddelde studietijd voor WFT Schade Particulier is 80 tot 120 uur, afhankelijk van je voorkennis en ervaring in de verzekeringsbranche. Als je al werkzaam bent in de sector en dagelijks met particuliere verzekeringen werkt, kun je mogelijk met 60-80 uur toe. Zonder voorkennis of ervaring is 100-120 uur realistischer. Het is aan te raden om de studie te spreiden over 6-10 weken, met gemiddeld 10-15 uur per week. Focus niet alleen op het lezen van theorie, maar besteed minstens 30% van je tijd aan het maken van oefenvragen en proefexamens. Dit helpt je om de vraagstelling te leren kennen en je kennis toe te passen op praktijksituaties, wat essentieel is voor de casus-vragen.'
      },
      {
        question: 'Wat is het slagingspercentage voor WFT Schade Particulier?',
        answer: 'Het slagingspercentage voor WFT Schade Particulier ligt gemiddeld rond de 45-50%, wat betekent dat ongeveer de helft van de kandidaten bij de eerste poging zakt. Dit maakt het een van de uitdagendere WFT modules. De voornaamste redenen voor zakken zijn onvoldoende voorbereiding, onderschatting van de praktijkvragen (casussen), en tijdgebrek tijdens het examen. De VC-vragen met casussen kosten relatief veel tijd om te lezen en te analyseren. Kandidaten die specifiek oefenen met proefexamens en tijdmanagement toepassen, hebben een significant hogere slagingskans.'
      },
      {
        question: 'Wat is het verschil tussen WFT Schade Particulier en Schade Zakelijk?',
        answer: 'WFT Schade Particulier richt zich op verzekeringen voor consumenten en huishoudens, terwijl WFT Schade Zakelijk gaat over verzekeringen voor bedrijven en ondernemers. Bij Schade Particulier leer je over inboedel-, opstal-, auto- en aansprakelijkheidsverzekeringen voor particulieren. Bij Schade Zakelijk komen bedrijfsmatige risico\'s aan bod zoals bedrijfsaansprakelijkheid (AVB), bestuurdersaansprakelijkheid, bedrijfsschadeverzekeringen, transportverzekeringen en technische verzekeringen. De zakelijke verzekeringen zijn vaak complexer, met hogere verzekerde bedragen, specifiekere uitsluitingen en meer maatwerk. Om Schade Zakelijk te mogen doen, moet je eerst Schade Particulier hebben behaald. Beide modules bouwen voort op WFT Basis.'
      }
    ],
    
    meta: {
      title: 'WFT Schade Particulier Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je optimaal voor op het WFT Schade Particulier examen met AI-gegenereerde oefenvragen op basis van officiële CDFD toetstermen. Proefexamens, samenvattingen en meer. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-schade-particulier.webp'
    },
    
    isActive: true,
    hasQuestions: true,
    hasSummaries: true
  },
  
  'schade-zakelijk': {
    id: 'schade-zakelijk',
    slug: 'wft-schade-zakelijk',
    name: 'Schade Zakelijk',
    fullName: 'WFT Schade Zakelijk',
    shortDescription: 'Verzekeringen voor bedrijven: brand, aansprakelijkheid, transport en meer.',
    longDescription: 'Het WFT Schade Zakelijk examen toetst je kennis over schadeverzekeringen voor bedrijven en ondernemers. Dit omvat bedrijfsschadeverzekeringen, aansprakelijkheidsverzekeringen voor bedrijven (AVB), transportverzekeringen, technische verzekeringen, en specialistische zakelijke polissen. Je leert ondernemers adviseren over risicobeheer en passende dekkingen.',
    
    specs: {
      totalQuestions: 55,
      duration: 120,
      passingScore: 68,
      passingPoints: 58,
      maxPoints: 85,
      kbQuestions: 25,
      kbPoints: 25,
      vcQuestions: 29,
      vcPoints: 58,
      pgQuestions: 1,
      pgPoints: 2
    },
    
    pricing: {
      examCost: 175,
      retakeCost: 175
    },
    
    topics: [
      'Bedrijfsschadeverzekering',
      'Aansprakelijkheidsverzekering Bedrijven (AVB)',
      'Beroepsaansprakelijkheidsverzekering',
      'Bestuurdersaansprakelijkheidsverzekering',
      'Transportverzekering',
      'Goederenverzekering',
      'Technische verzekeringen (CAR/Montage)',
      'Machinebreukverzekering',
      'Elektronicaverzekering',
      'Reconstructiekostenverzekering',
      'Milieuaansprakelijkheidsverzekering',
      'Kredietverzekering',
      'Fraudeverzekering'
    ],
    
    keywords: [
      'wft schade zakelijk',
      'wft schade zakelijk oefenvragen',
      'wft schade zakelijk examen',
      'wft zakelijk verzekeringen',
      'bedrijfsverzekeringen wft',
      'cdfd schade zakelijk'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Schade Zakelijk examen?',
        answer: 'Het WFT Schade Zakelijk examen bestaat uit 55 vragen verdeeld over drie categorieën: 25 Kennis & Begrip vragen (1 punt elk), 29 Vaardigheden & Competenties vragen (2 punten elk), en 1 Professioneel Gedrag vraag (2 punten). Het maximaal te behalen puntenaantal is 85 punten. Om te slagen heb je minimaal 58 punten nodig, wat overeenkomt met de slaaggrens van 68%. Je hebt 120 minuten (2 uur) de tijd om alle vragen te beantwoorden. Deze samenstelling geldt voor examens vanaf 1 april 2026.'
      },
      {
        question: 'Welke onderwerpen komen aan bod bij WFT Schade Zakelijk?',
        answer: 'WFT Schade Zakelijk behandelt alle schadeverzekeringen voor bedrijven en ondernemers. De hoofdonderwerpen zijn: bedrijfsaansprakelijkheidsverzekering (AVB), beroepsaansprakelijkheidsverzekering, bestuurdersaansprakelijkheidsverzekering (D&O), bedrijfsschadeverzekering (gevolgen van stilstand), brandverzekering voor bedrijven, elektronicaverzekering, transportverzekeringen (goederen en CMR), technische verzekeringen (CAR, montage, machinebreuk), milieuaansprakelijkheidsverzekering, en kredietverzekering. Je moet begrijpen hoe je risico\'s voor ondernemingen inventariseert, welke dekkingen passend zijn per branche, en hoe je complexe polisvoorwaarden interpreteert.'
      },
      {
        question: 'Moet ik eerst WFT Schade Particulier halen voordat ik Schade Zakelijk mag doen?',
        answer: 'Ja, WFT Schade Particulier is een verplichte vooropleiding voor WFT Schade Zakelijk. Dit is vastgelegd in het vakbekwaamheidsbouwwerk van de Wft. De logica hierachter is dat Schade Zakelijk voortbouwt op de basiskennis van particuliere schadeverzekeringen. Concepten zoals aansprakelijkheid, polisvoorwaarden en schadeafhandeling leer je eerst in de particuliere context, waarna je deze kennis toepast op complexere zakelijke situaties. De volgorde is dus: eerst WFT Basis, dan WFT Schade Particulier, en daarna pas WFT Schade Zakelijk. Sommige kandidaten kiezen ervoor om beide schade-examens kort na elkaar te plannen om de overlap in kennis optimaal te benutten.'
      },
      {
        question: 'Wat is het verschil tussen AVB en beroepsaansprakelijkheid?',
        answer: 'De Aansprakelijkheidsverzekering Bedrijven (AVB) dekt schade die het bedrijf veroorzaakt aan derden door bedrijfsactiviteiten, zoals een bezoeker die uitglijdt op een natte vloer of schade door een geleverd product. De beroepsaansprakelijkheidsverzekering dekt daarentegen vermogensschade die ontstaat door fouten in de beroepsuitoefening, zoals een verkeerd advies van een accountant of een fout in een ontwerp van een architect. Bij AVB gaat het vaak om letsel- en zaakschade, bij beroepsaansprakelijkheid om zuivere vermogensschade. Veel bedrijven hebben beide verzekeringen nodig, afhankelijk van hun activiteiten.'
      },
      {
        question: 'Hoe lang duurt het WFT Schade Zakelijk examen?',
        answer: 'Het WFT Schade Zakelijk examen duurt 120 minuten (2 uur). In deze tijd moet je alle 55 vragen beantwoorden. De vragen worden in een vaste volgorde aangeboden. Het is belangrijk om je tijd goed te verdelen: gemiddeld heb je ruim 2 minuten per vraag. De VC-vragen met casussen kosten vaak meer tijd, dus zorg dat je bij de KB-vragen niet te lang blijft hangen. Een goede strategie is om eerst alle vragen door te lopen, de makkelijke vragen direct te beantwoorden, en daarna terug te gaan naar de moeilijkere vragen.'
      }
    ],
    
    meta: {
      title: 'WFT Schade Zakelijk Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je voor op het WFT Schade Zakelijk examen met AI-gegenereerde oefenvragen gebaseerd op officiële CDFD toetstermen. Zakelijke verzekeringen onder de knie krijgen. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-schade-zakelijk.webp'
    },
    
    isActive: true,
    hasQuestions: true,
    hasSummaries: true
  },
  
  'basis': {
    id: 'basis',
    slug: 'wft-basis',
    name: 'Basis',
    fullName: 'WFT Basis',
    shortDescription: 'De basis van financiële dienstverlening: wet- en regelgeving, integriteit en klantbelang.',
    longDescription: 'Het WFT Basis examen is het fundament voor alle WFT-diploma\'s. Het toetst je kennis over de Wet op het financieel toezicht, integriteit, klantbelang centraal, en de basisprincipes van financiële producten. Dit examen is verplicht voor iedereen die in de financiële sector wil werken.',
    
    specs: {
      totalQuestions: 42,
      duration: 120,
      passingScore: 68,
      passingPoints: 43,
      maxPoints: 63,
      kbQuestions: 21,
      kbPoints: 21,
      vcQuestions: 19,
      vcPoints: 38,
      pgQuestions: 2,
      pgPoints: 4
    },
    
    pricing: {
      examCost: 175,
      retakeCost: 175
    },
    
    topics: [
      'Wet op het financieel toezicht (Wft)',
      'Autoriteit Financiële Markten (AFM)',
      'De Nederlandsche Bank (DNB)',
      'Integriteit en ethiek',
      'Klantbelang centraal',
      'Zorgplicht',
      'Passend advies',
      'Informatieverstrekking',
      'Wwft (Wet witwassen)',
      'Sanctiewet',
      'Klachtenbehandeling',
      'Financiële basisproducten'
    ],
    
    keywords: [
      'wft basis',
      'wft basis examen',
      'wft basis oefenvragen',
      'wft basis samenvatting',
      'wft basis halen',
      'cdfd basis examen'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Basis examen?',
        answer: 'Het WFT Basis examen bestaat sinds 1 april 2026 uit 42 vragen die je in 120 minuten moet beantwoorden. De vragen zijn verdeeld over drie categorieën: 21 Kennis & Begrip (KB) vragen van elk 1 punt, 19 Vaardigheden & Competenties (VC) vragen van elk 2 punten, en 2 Professioneel Gedrag (PG) vragen van elk 2 punten. Het maximaal te behalen puntenaantal is dus 63. Om te slagen heb je minimaal 43 punten nodig, wat overeenkomt met de slaaggrens van 68%. Net als bij de andere modules wegen de VC- en PG-vragen dus dubbel.'
      },
      {
        question: 'Is WFT Basis moeilijk?',
        answer: 'WFT Basis wordt over het algemeen beschouwd als een van de toegankelijkere WFT modules. Volgens de CDFD-statistieken over april tot en met september 2026 slaagde 56% van de afgelegde Basis-examens, na Zorgverzekeringen het hoogste percentage van alle modules. Dit komt doordat de stof meer theoretisch en algemeen van aard is, zonder complexe berekeningen of uitgebreide casussen. Toch moet je het examen niet onderschatten. De vragen over wet- en regelgeving, toezichthouders (AFM, DNB), de Wwft (Wet witwassen) en klantbelang centraal vereisen nauwkeurige kennis van definities en procedures. De gemiddelde studietijd is 40-60 uur. Het grootste struikelblok is vaak de hoeveelheid regelgeving die je moet onthouden. Met voldoende oefening en het maken van proefexamens is WFT Basis goed te behalen.'
      },
      {
        question: 'Welke onderwerpen komen aan bod bij WFT Basis?',
        answer: 'WFT Basis behandelt de fundamenten van de financiële dienstverlening in Nederland. De kernonderwerpen zijn: de Wet op het financieel toezicht (Wft) en de rol van toezichthouders AFM en DNB, integriteit en ethisch handelen, het principe van klantbelang centraal, zorgplicht en passend advies, informatieverstrekking aan klanten, de Wet ter voorkoming van witwassen en financieren van terrorisme (Wwft), de Sanctiewet, klachtenbehandeling en geschillenbeslechting, en een introductie tot financiële basisproducten. Je leert ook over de verschillende vergunningen en registers in de financiële sector. Deze basiskennis is essentieel voor alle verdere WFT modules en vormt het fundament voor je carrière als financieel adviseur.'
      },
      {
        question: 'Is WFT Basis verplicht voor alle financieel adviseurs?',
        answer: 'Ja, WFT Basis is verplicht voor iedereen die consumenten adviseert over financiële producten in Nederland. Het is de basismodule die je nodig hebt voordat je de specialistische modules (zoals Schade, Hypotheken, Vermogen) kunt halen. De wet schrijft voor dat financieel adviseurs vakbekwaam moeten zijn, en WFT Basis toetst de fundamentele kennis die elke adviseur moet hebben. Dit omvat kennis van wet- en regelgeving, integriteit, en het principe van klantbelang centraal. Zonder WFT Basis mag je geen adviesgesprekken voeren met klanten over financiële producten. Het diploma vormt de basis van het vakbekwaamheidsbouwwerk in de financiële sector.'
      },
      {
        question: 'Hoe lang duurt het WFT Basis examen?',
        answer: 'Het WFT Basis examen duurt 120 minuten (2 uur). Met 42 vragen in 120 minuten heb je gemiddeld bijna 3 minuten per vraag, en de VC-vragen met casussen vragen daarvan het meeste. Dit is voldoende tijd als je je goed hebt voorbereid. De meeste kandidaten hebben tijd over om hun antwoorden te controleren. Het is aan te raden om eerst alle vragen door te lopen, de vragen waar je zeker van bent direct te beantwoorden, en daarna terug te gaan naar de vragen waar je over twijfelt.'
      }
    ],
    
    meta: {
      title: 'WFT Basis Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je voor op het WFT Basis examen met AI-gegenereerde oefenvragen. Leer de Wft, integriteit en klantbelang centraal. Officiële CDFD toetstermen. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-basis.webp'
    },
    
    isActive: true,
    hasQuestions: false,
    hasSummaries: false
  },
  
  'hypotheken': {
    id: 'hypotheken',
    slug: 'wft-hypotheken',
    name: 'Hypothecair Krediet',
    fullName: 'WFT Hypothecair Krediet',
    shortDescription: 'Hypotheken en woningfinanciering: hypotheekvormen, fiscaliteit en acceptatie.',
    longDescription: 'Het WFT Hypothecair Krediet examen toetst je kennis over hypotheken en woningfinanciering. Dit omvat verschillende hypotheekvormen, renteberekeningen, fiscale aspecten, acceptatiecriteria, en Nationale Hypotheek Garantie. Je leert klanten adviseren over de beste hypotheekoplossing voor hun situatie.',
    
    specs: {
      totalQuestions: 37,
      duration: 135,
      passingScore: 68,
      passingPoints: 41,
      maxPoints: 60,
      kbQuestions: 14,
      kbPoints: 14,
      vcQuestions: 22,
      vcPoints: 44,
      pgQuestions: 1,
      pgPoints: 2
    },
    
    pricing: {
      examCost: 195,
      retakeCost: 195
    },
    
    topics: [
      'Hypotheekvormen (annuïteit, lineair, aflossingsvrij)',
      'Rentevormen en rentevaste periodes',
      'Hypotheekrente aftrek',
      'Eigenwoningforfait',
      'Nationale Hypotheek Garantie (NHG)',
      'Loan-to-Value (LTV)',
      'Loan-to-Income (LTI)',
      'NIBUD-normen',
      'Oversluiten en verhogen',
      'Restschuld',
      'Executieverkoop',
      'Notariële afwikkeling'
    ],
    
    keywords: [
      'wft hypotheken',
      'wft hypothecair krediet',
      'wft hypotheken oefenvragen',
      'wft hypotheken examen',
      'hypotheekadviseur worden',
      'cdfd hypotheken'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Hypothecair Krediet examen?',
        answer: 'Het WFT Hypothecair Krediet examen bestaat uit 37 vragen die je in 135 minuten (2 uur en 15 minuten) moet beantwoorden. De verdeling is: 14 Kennis & Begrip (KB) vragen van elk 1 punt, 22 Vaardigheden & Competenties (VC) vragen van elk 2 punten, en 1 Professioneel Gedrag (PG) vraag van 2 punten. Het maximaal te behalen puntenaantal is 60. Om te slagen heb je minimaal 41 punten nodig (68%). Dit examen bevat relatief veel rekenvragen over hypotheekberekeningen, maandlasten, en fiscale aspecten. Je mag een niet-programmeerbare rekenmachine en het fiscaal memo gebruiken tijdens het examen.'
      },
      {
        question: 'Welke berekeningen moet ik kunnen maken voor WFT Hypotheken?',
        answer: 'Voor het WFT Hypothecair Krediet examen moet je verschillende berekeningen beheersen. De belangrijkste zijn: bruto en netto maandlasten berekenen, annuïteiten en lineaire aflossingen doorrekenen, de maximale hypotheek bepalen op basis van inkomen en leennormen, de loan-to-value (LTV) ratio berekenen, fiscale aftrek berekenen inclusief eigenwoningforfait, de bijleenregeling toepassen, en rendementsberekeningen voor beleggingshypotheken. Je moet ook kunnen rekenen met de NHG-kostengrens en begrijpen wanneer NHG van toepassing is. Tijdens het examen krijg je een fiscaal memo met actuele percentages en grenzen. Oefen vooral met het snel en accuraat uitvoeren van deze berekeningen, want tijdmanagement is cruciaal bij dit examen.'
      },
      {
        question: 'Hoe lang moet ik studeren voor WFT Hypotheken?',
        answer: 'De gemiddelde studietijd voor WFT Hypothecair Krediet is 100 tot 150 uur. Dit is een van de meer uitgebreide WFT modules vanwege de combinatie van theoretische kennis, wet- en regelgeving, en rekenvaardigheden. Als je al ervaring hebt als hypotheekadviseur, kun je mogelijk met 80-100 uur toe. Zonder voorkennis is 120-150 uur realistischer. Verdeel je studie over 8-12 weken. Besteed extra aandacht aan de rekenkundige onderdelen door veel te oefenen met berekeningen. Veel kandidaten onderschatten de hoeveelheid regelgeving rondom hypotheken, zoals de Gedragscode Hypothecaire Financieringen, NHG-voorwaarden, en fiscale regels. Maak minimaal 5 volledige proefexamens onder examenomstandigheden om je tijdsindeling te oefenen.'
      },
      {
        question: 'Wat is de Nationale Hypotheek Garantie (NHG)?',
        answer: 'De Nationale Hypotheek Garantie (NHG) is een garantie van de Stichting Waarborgfonds Eigen Woningen die huizenkopers beschermt tegen restschuld bij gedwongen verkoop. Als je door omstandigheden buiten je schuld (zoals werkloosheid, arbeidsongeschiktheid of scheiding) je hypotheek niet meer kunt betalen en je huis wordt verkocht voor minder dan de hypotheekschuld, dan keert NHG de restschuld uit aan de geldverstrekker. Voor de consument betekent dit minder risico. Geldverstrekkers bieden daarom een lagere rente bij NHG-hypotheken (gemiddeld 0,3-0,6% lager). NHG is mogelijk tot een bepaalde kostengrens (in 2025 rond €435.000) en je betaalt eenmalig een borgtochtprovisie van 0,6% van de hypotheek.'
      },
      {
        question: 'Wat is het verschil tussen annuïteitenhypotheek en lineaire hypotheek?',
        answer: 'Bij een annuïteitenhypotheek betaal je elke maand hetzelfde bruto bedrag (annuïteit), bestaande uit rente plus aflossing. In het begin is de rentecomponent groot en de aflossing klein; aan het einde is dat omgekeerd. Bij een lineaire hypotheek los je elke maand een vast bedrag af, waardoor de schuld gelijkmatig daalt. De rente wordt berekend over de restschuld, dus je maandlasten dalen in de loop der tijd. Fiscaal gezien is de annuïteitenhypotheek in het begin voordeliger door de hogere renteaftrek, maar over de gehele looptijd betaal je meer rente dan bij lineair. De lineaire hypotheek is goedkoper in totale rentekosten, maar begint met hogere maandlasten. Sinds 2013 moet je voor hypotheekrenteaftrek verplicht annuïtair of lineair aflossen.'
      }
    ],
    
    meta: {
      title: 'WFT Hypothecair Krediet Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je voor op het WFT Hypothecair Krediet examen met AI-gegenereerde oefenvragen. Hypotheekvormen, fiscaliteit en acceptatie. Officiële CDFD toetstermen. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-hypotheken.webp'
    },
    
    isActive: true,
    hasQuestions: false,
    hasSummaries: false
  },
  
  'vermogen': {
    id: 'vermogen',
    slug: 'wft-vermogen',
    name: 'Vermogen',
    fullName: 'WFT Vermogen',
    shortDescription: 'Beleggen en vermogensopbouw: effecten, fondsen, risicoprofielen en fiscaliteit.',
    longDescription: 'Het WFT Vermogen examen toetst je kennis over beleggingen en vermogensopbouw. Dit omvat verschillende beleggingsproducten, risicoprofielen, portefeuilletheorie, en fiscale aspecten van vermogen. Je leert klanten adviseren over passende beleggingsstrategieën.',
    
    specs: {
      totalQuestions: 46,
      duration: 135,
      passingScore: 68,
      passingPoints: 49,
      maxPoints: 72,
      kbQuestions: 20,
      kbPoints: 20,
      vcQuestions: 25,
      vcPoints: 50,
      pgQuestions: 1,
      pgPoints: 2
    },
    
    pricing: {
      examCost: 195,
      retakeCost: 195
    },
    
    topics: [
      'Aandelen en obligaties',
      'Beleggingsfondsen',
      'ETF\'s en trackers',
      'Derivaten (opties, futures)',
      'Risicoprofielen',
      'Portefeuilletheorie',
      'Box 3 belasting',
      'Dividendbelasting',
      'Vermogensrendementsheffing',
      'Duurzaam beleggen (ESG)',
      'Execution only vs. advies',
      'MiFID II regelgeving'
    ],
    
    keywords: [
      'wft vermogen',
      'wft vermogen examen',
      'wft vermogen oefenvragen',
      'wft beleggen',
      'beleggingsadviseur worden',
      'cdfd vermogen'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Vermogen examen?',
        answer: 'Het WFT Vermogen examen bestaat uit 46 vragen die je in 135 minuten (2 uur en 15 minuten) moet beantwoorden. De verdeling is: 20 Kennis & Begrip (KB) vragen van elk 1 punt, 25 Vaardigheden & Competenties (VC) vragen van elk 2 punten, en 1 Professioneel Gedrag (PG) vraag van 2 punten. Het maximaal te behalen puntenaantal is 72. Om te slagen heb je minimaal 49 punten nodig (68%). Het examen bevat vragen over verschillende beleggingsproducten, risicobeheer, en de MiFID II regelgeving.'
      },
      {
        question: 'Welke onderwerpen komen aan bod bij WFT Vermogen?',
        answer: 'WFT Vermogen behandelt alles rondom beleggen en vermogensopbouw voor particulieren. De hoofdonderwerpen zijn: verschillende beleggingsproducten (aandelen, obligaties, fondsen, ETF\'s, derivaten), risicoprofilering en de relatie tussen risico en rendement, moderne portefeuilletheorie en spreiding, fiscale aspecten van vermogen (box 3, vrijstellingen), pensioenopbouw via beleggen, de werking van financiële markten, en gedragseconomie (behavioral finance). Je moet klanten kunnen adviseren over vermogensopbouw passend bij hun doelen, horizon en risicoprofiel. Ook execution-only dienstverlening en de MiFID II-regelgeving komen aan bod. Dit examen vereist begrip van financiële markten en de kunde om complexe producten begrijpelijk uit te leggen aan klanten.'
      },
      {
        question: 'Wat is het verschil tussen execution only en advies bij beleggen?',
        answer: 'Bij execution only voert de beleggingsonderneming alleen orders uit die de klant zelf aandraagt, zonder advies te geven over de geschiktheid van de belegging. De klant is zelf verantwoordelijk voor zijn keuzes. Bij advies beoordeelt de adviseur of een belegging past bij de doelstellingen, financiële situatie, kennis en ervaring van de klant (geschiktheidstoets). De adviseur heeft een zorgplicht en moet passend advies geven. Bij vermogensbeheer gaat de beheerder nog een stap verder en neemt zelfstandig beleggingsbeslissingen binnen het afgesproken mandaat. De MiFID II regelgeving stelt verschillende eisen aan deze diensten, met de zwaarste eisen bij vermogensbeheer en advies.'
      },
      {
        question: 'Hoe werkt box 3 belasting op vermogen?',
        answer: 'In box 3 wordt belasting geheven over vermogen boven de vrijstelling (in 2025 ruim €57.000 per persoon, voor fiscaal partners het dubbele). De belasting wordt berekend over een fictief rendement dat afhankelijk is van de samenstelling van je vermogen: spaargeld wordt lager belast dan beleggingen. Het fictieve rendement wordt belast tegen een vast tarief van 36%. Dit systeem is in ontwikkeling omdat de Hoge Raad heeft geoordeeld dat het heffen op basis van fictief rendement in strijd kan zijn met het eigendomsrecht als het werkelijke rendement lager is. Voor adviseurs is het belangrijk om klanten te informeren over de fiscale gevolgen van verschillende vermogensvormen en de mogelijkheden om belasting te optimaliseren binnen de wettelijke kaders.'
      },
      {
        question: 'Wat is MiFID II en waarom is het belangrijk?',
        answer: 'MiFID II (Markets in Financial Instruments Directive II) is de Europese richtlijn die de beleggingsmarkt reguleert. De richtlijn is ingevoerd om beleggers beter te beschermen en de transparantie in financiële markten te vergroten. Voor adviseurs betekent MiFID II onder andere: verplichte geschiktheidstoets bij advies, transparantie over kosten en vergoedingen, een verbod op inducements (beloningen van productaanbieders) bij onafhankelijk advies, en uitgebreide vastleggingsverplichtingen. Bij het WFT Vermogen examen moet je weten welke eisen MiFID II stelt aan verschillende diensten (execution only, advies, vermogensbeheer), hoe je de geschiktheidstoets uitvoert, en welke informatie je aan klanten moet verstrekken. Het niet naleven van MiFID II kan leiden tot sancties van de AFM.'
      }
    ],
    
    meta: {
      title: 'WFT Vermogen Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je voor op het WFT Vermogen examen met AI-gegenereerde oefenvragen. Beleggen, fondsen en fiscaliteit. Officiële CDFD toetstermen. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-vermogen.webp'
    },
    
    isActive: true,
    hasQuestions: false,
    hasSummaries: false
  },
  
  'inkomen': {
    id: 'inkomen',
    slug: 'wft-inkomen',
    name: 'Inkomen',
    fullName: 'WFT Inkomen',
    shortDescription: 'Inkomensverzekeringen: arbeidsongeschiktheid, werkloosheid en sociale zekerheid.',
    longDescription: 'Het WFT Inkomen examen toetst je kennis over inkomensverzekeringen en sociale zekerheid. Dit omvat arbeidsongeschiktheidsverzekeringen, werknemersverzekeringen, en aanvullingen op de sociale zekerheid. Je leert klanten adviseren over inkomensbescherming.',
    
    specs: {
      totalQuestions: 44,
      duration: 135,
      passingScore: 68,
      passingPoints: 47,
      maxPoints: 68,
      kbQuestions: 20,
      kbPoints: 20,
      vcQuestions: 23,
      vcPoints: 46,
      pgQuestions: 1,
      pgPoints: 2
    },
    
    pricing: {
      examCost: 195,
      retakeCost: 195
    },
    
    topics: [
      'Arbeidsongeschiktheidsverzekering (AOV)',
      'WIA (WGA en IVA)',
      'Ziektewet',
      'WW (Werkloosheidswet)',
      'Loondoorbetaling bij ziekte',
      'Verzuimverzekering',
      'WGA-hiaatverzekering',
      'WIA-excedentverzekering',
      'Wajong',
      'Participatiewet',
      'Eigenrisicodragerschap',
      'Woonlastenverzekering'
    ],
    
    keywords: [
      'wft inkomen',
      'wft inkomen examen',
      'wft inkomen oefenvragen',
      'wft arbeidsongeschiktheid',
      'wft aov',
      'cdfd inkomen'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Inkomen examen?',
        answer: 'Het WFT Inkomen examen bestaat uit 44 vragen die je in 135 minuten (2 uur en 15 minuten) moet beantwoorden. De verdeling is: 20 Kennis & Begrip (KB) vragen van elk 1 punt, 23 Vaardigheden & Competenties (VC) vragen van elk 2 punten, en 1 Professioneel Gedrag (PG) vraag van 2 punten. Het maximaal te behalen puntenaantal is 68. Om te slagen heb je minimaal 47 punten nodig (68%).'
      },
      {
        question: 'Welke onderwerpen komen aan bod bij WFT Inkomen?',
        answer: 'WFT Inkomen richt zich op verzekeringen die inkomen beschermen bij arbeidsongeschiktheid of werkloosheid. De hoofdonderwerpen zijn: de sociale zekerheid in Nederland (WIA, WW, Ziektewet), arbeidsongeschiktheidsverzekeringen (AOV) voor zelfstandigen en ondernemers, WGA-hiaatverzekeringen, WIA-excedentverzekeringen, de Participatiewet, loondoorbetaling bij ziekte, verzuimverzekeringen voor werkgevers, en woonlastenverzekeringen. Je moet kunnen adviseren welke aanvullingen op sociale zekerheid nodig zijn afhankelijk van de arbeidssituatie (loondienst, zzp, DGA). Ook actuariële begrippen zoals uitkeringsdrempels, eigenrisicoperiodes en dekkingsvormen komen uitgebreid aan bod.'
      },
      {
        question: 'Wat is het verschil tussen WGA en IVA?',
        answer: 'WGA (Werkhervatting Gedeeltelijk Arbeidsgeschikten) en IVA (Inkomensvoorziening Volledig Arbeidsongeschikten) zijn beide onderdeel van de WIA (Wet werk en inkomen naar arbeidsvermogen). De WGA is voor werknemers die gedeeltelijk arbeidsongeschikt zijn (35-80%) of volledig maar niet duurzaam arbeidsongeschikt. De IVA is voor werknemers die volledig én duurzaam arbeidsongeschikt zijn (80-100% zonder uitzicht op herstel). De IVA-uitkering is hoger (75% van het laatstverdiende loon) en loopt door tot de AOW-leeftijd. De WGA-uitkering is complexer met een loongerelateerde fase gevolgd door een loonaanvulling of vervolguitkering, afhankelijk van hoeveel je nog verdient. Voor adviseurs is het cruciaal om te weten wanneer welke regeling van toepassing is en welke aanvullingen klanten nodig hebben.'
      },
      {
        question: 'Waarom hebben zzp\'ers een AOV nodig?',
        answer: 'Zelfstandigen zonder personeel (zzp\'ers) vallen niet onder de werknemersverzekeringen zoals de WIA en Ziektewet. Als een zzp\'er arbeidsongeschikt wordt, heeft hij geen recht op een uitkering vanuit de overheid (behalve eventueel bijstand na een vermogenstoets). Een Arbeidsongeschiktheidsverzekering (AOV) biedt bescherming door een uitkering te verstrekken als de zzp\'er door ziekte of ongeval niet kan werken. De hoogte van de premie hangt af van factoren zoals beroep, leeftijd, gewenst verzekerd bedrag en eigenrisicoperiode. Zonder AOV kan arbeidsongeschiktheid leiden tot grote financiële problemen, vooral als de zzp\'er vaste lasten heeft zoals een hypotheek. Een goede adviseur helpt zzp\'ers om de juiste dekking te kiezen passend bij hun situatie en budget.'
      },
      {
        question: 'Wat is een WGA-hiaatverzekering?',
        answer: 'Een WGA-hiaatverzekering is een aanvullende verzekering die het inkomensgat dicht dat ontstaat bij gedeeltelijke arbeidsongeschiktheid in de WGA. De wettelijke WGA-uitkering is gebaseerd op het minimumloon of een percentage van het restverdienvermogen, wat vaak een forse inkomensdaling betekent ten opzichte van het oorspronkelijke salaris. De WGA-hiaatverzekering vult dit aan tot een hoger percentage van het oude inkomen. Er zijn verschillende varianten: de WGA-hiaatverzekering (voor het verschil tussen uitkering en oude loon tot de WIA-grens) en de WGA-excedentverzekering (voor inkomens boven de WIA-grens). Voor werknemers met een modaal of hoger inkomen is dit een belangrijke aanvulling om hun levensstandaard te kunnen behouden bij arbeidsongeschiktheid.'
      }
    ],
    
    meta: {
      title: 'WFT Inkomen Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je voor op het WFT Inkomen examen met AI-gegenereerde oefenvragen. AOV, WIA en sociale zekerheid. Officiële CDFD toetstermen. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-inkomen.webp'
    },
    
    isActive: true,
    hasQuestions: false,
    hasSummaries: false
  },

  'zorgverzekeringen': {
    id: 'zorgverzekeringen',
    slug: 'wft-zorgverzekeringen',
    name: 'Zorgverzekeringen',
    fullName: 'WFT Zorgverzekeringen',
    shortDescription: 'Zorgverzekeringen: basisverzekering, aanvullende verzekeringen en Wlz.',
    longDescription: 'Het WFT Zorgverzekeringen examen toetst je kennis over het Nederlandse zorgstelsel. Dit omvat de verplichte basisverzekering, aanvullende verzekeringen, eigen risico, en langdurige zorg (Wlz). Je leert klanten adviseren over de juiste zorgverzekering.',
    
    specs: {
      totalQuestions: 33,
      duration: 90,
      passingScore: 68,
      passingPoints: 36,
      maxPoints: 52,
      kbQuestions: 14,
      kbPoints: 14,
      vcQuestions: 18,
      vcPoints: 36,
      pgQuestions: 1,
      pgPoints: 2
    },
    
    pricing: {
      examCost: 145,
      retakeCost: 145
    },
    
    topics: [
      'Zorgverzekeringswet (Zvw)',
      'Basisverzekering',
      'Aanvullende verzekering',
      'Eigen risico',
      'Eigen bijdrage',
      'Zorgtoeslag',
      'Wet langdurige zorg (Wlz)',
      'Wmo (Wet maatschappelijke ondersteuning)',
      'Jeugdwet',
      'Contracteerbeleid',
      'Naturapolis vs. restitutiepolis',
      'Collectieve verzekeringen'
    ],
    
    keywords: [
      'wft zorgverzekeringen',
      'wft zorg examen',
      'wft zorgverzekeringen oefenvragen',
      'zorgverzekering adviseur',
      'cdfd zorgverzekeringen'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Zorgverzekeringen examen?',
        answer: 'Het WFT Zorgverzekeringen examen bestaat uit 33 vragen die je in 90 minuten (1,5 uur) moet beantwoorden. De verdeling is: 14 Kennis & Begrip (KB) vragen van elk 1 punt, 18 Vaardigheden & Competenties (VC) vragen van elk 2 punten, en 1 Professioneel Gedrag (PG) vraag van 2 punten. Het maximaal te behalen puntenaantal is 52. Om te slagen heb je minimaal 36 punten nodig (68%). Dit is een van de kortere WFT examens.'
      },
      {
        question: 'Wat leer ik bij WFT Zorgverzekeringen?',
        answer: 'WFT Zorgverzekeringen behandelt het Nederlandse zorgstelsel en de advisering over zorgverzekeringen. De hoofdonderwerpen zijn: de Zorgverzekeringswet (Zvw) en de verplichte basisverzekering, aanvullende zorgverzekeringen en tandartsverzekeringen, de Wet langdurige zorg (Wlz), de Wet maatschappelijke ondersteuning (Wmo), het eigen risico en eigen bijdragen, zorgtoeslag en inkomensafhankelijke bijdragen, collectieve zorgverzekeringen, en de werking van het zorgstelsel met zorgverzekeraars, zorgaanbieders en de Nederlandse Zorgautoriteit. Je leert klanten adviseren over de juiste basis- en aanvullende verzekering passend bij hun zorgbehoefte.'
      },
      {
        question: 'Wat is het verschil tussen een naturapolis en een restitutiepolis?',
        answer: 'Bij een naturapolis sluit de zorgverzekeraar contracten met zorgaanbieders. Als je naar een gecontracteerde zorgaanbieder gaat, wordt de zorg volledig vergoed. Ga je naar een niet-gecontracteerde aanbieder, dan wordt vaak maar een deel vergoed (bijvoorbeeld 75%). Bij een restitutiepolis heb je vrije zorgkeuze: je kunt naar elke zorgaanbieder en krijgt de kosten (tot het marktconforme tarief) vergoed. Een combinatiepolis combineert elementen van beide. Naturapolis is meestal goedkoper in premie maar beperkter in keuze. Restitutiepolis is duurder maar biedt meer vrijheid. Voor adviseurs is het belangrijk om te weten welke polisvorm past bij de zorgbehoefte en voorkeuren van de klant.'
      },
      {
        question: 'Hoe werkt het eigen risico bij de zorgverzekering?',
        answer: 'Het verplicht eigen risico is het bedrag dat je zelf betaalt voordat de zorgverzekeraar kosten vergoedt. In 2025 is dit €385 per jaar voor volwassenen (18+). Kinderen tot 18 jaar hebben geen eigen risico. Het eigen risico geldt voor de meeste zorg uit de basisverzekering, maar niet voor huisartsenzorg, verloskundige zorg, en kraamzorg. Je kunt kiezen voor een vrijwillig verhoogd eigen risico (tot €500 extra) in ruil voor lagere premie. Het eigen risico wordt per kalenderjaar berekend en telt niet mee voor de aanvullende verzekering. Adviseurs moeten klanten helpen om af te wegen of een hoger eigen risico financieel verstandig is, afhankelijk van hun zorggebruik.'
      },
      {
        question: 'Wanneer heb je recht op zorgtoeslag?',
        answer: 'Zorgtoeslag is een bijdrage van de overheid in de kosten van de zorgverzekering voor mensen met een lager inkomen. Je hebt mogelijk recht op zorgtoeslag als je 18 jaar of ouder bent, een Nederlandse zorgverzekering hebt, en je inkomen onder een bepaalde grens ligt. Voor alleenstaanden ligt de grens rond €38.000, voor toeslagpartners samen rond €48.000 (bedragen kunnen jaarlijks wijzigen). De hoogte van de zorgtoeslag is inkomensafhankelijk en wordt maandelijks uitbetaald door de Belastingdienst/Toeslagen. Het vermogen telt ook mee bij de berekening. Adviseurs moeten klanten wijzen op de mogelijkheid van zorgtoeslag en hen helpen om deze aan te vragen als ze er recht op hebben.'
      }
    ],
    
    meta: {
      title: 'WFT Zorgverzekeringen Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je voor op het WFT Zorgverzekeringen examen met AI-gegenereerde oefenvragen. Basisverzekering, aanvullend en Wlz. Officiële CDFD toetstermen. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-zorg.webp'
    },
    
    isActive: true,
    hasQuestions: false,
    hasSummaries: false
  },

  'pensioen': {
    id: 'pensioen',
    slug: 'wft-pensioen',
    name: 'Pensioen',
    fullName: 'WFT Pensioen',
    shortDescription: 'Pensioen: pensioenstelsels, pensioenregelingen en fiscaliteit.',
    longDescription: 'Het WFT Pensioen examen toetst je kennis over het Nederlandse pensioenstelsel. Dit omvat de drie pijlers van pensioen, verschillende pensioenregelingen, fiscale aspecten, en de Pensioenwet. Je leert klanten adviseren over pensioenopbouw en -uitkering.',
    
    specs: {
      totalQuestions: 48,
      duration: 135,
      passingScore: 68,
      passingPoints: 51,
      maxPoints: 74,
      kbQuestions: 22,
      kbPoints: 22,
      vcQuestions: 25,
      vcPoints: 50,
      pgQuestions: 1,
      pgPoints: 2
    },
    
    pricing: {
      examCost: 195,
      retakeCost: 195
    },
    
    topics: [
      'Drie pijlers pensioenstelsel',
      'AOW (Algemene Ouderdomswet)',
      'Pensioenwet',
      'Wet toekomst pensioenen (Wtp)',
      'Pensioensoorten (ouderdoms-, nabestaanden-, arbeidsongeschiktheidspensioen)',
      'Pensioenregelingen (middelloon, beschikbare premie)',
      'Pensioenfondsen',
      'Premiepensioeninstelling (PPI)',
      'Waardeoverdracht',
      'Afkoop klein pensioen',
      'Pensioen en echtscheiding',
      'Fiscale behandeling pensioen'
    ],
    
    keywords: [
      'wft pensioen',
      'wft pensioenverzekeringen',
      'wft pensioen examen',
      'wft pensioen oefenvragen',
      'pensioenadviseur worden',
      'cdfd pensioen'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Pensioen examen?',
        answer: 'Het WFT Pensioen examen bestaat uit 48 vragen die je in 135 minuten (2 uur en 15 minuten) moet beantwoorden. De verdeling is: 22 Kennis & Begrip (KB) vragen van elk 1 punt, 25 Vaardigheden & Competenties (VC) vragen van elk 2 punten, en 1 Professioneel Gedrag (PG) vraag van 2 punten. Het maximaal te behalen puntenaantal is 74. Om te slagen heb je minimaal 51 punten nodig (68%). Dit is een van de langere WFT examens vanwege de complexiteit van pensioenberekeningen.'
      },
      {
        question: 'Wat leer ik bij WFT Pensioen?',
        answer: 'WFT Pensioen behandelt het Nederlandse pensioenstelsel en de advisering over pensioenvoorzieningen. De hoofdonderwerpen zijn: de drie pijlers van het pensioenstelsel (AOW, werkgeverspensioen, individueel pensioen), de Pensioenwet en de nieuwe Wet toekomst pensioenen (Wtp), pensioenregelingen (middelloon, beschikbare premie), pensioenuitvoerders (pensioenfondsen, verzekeraars, PPI\'s), waardeoverdracht en afkoop, nabestaandenpensioen, arbeidsongeschiktheidspensioen, lijfrentes en banksparen, en de fiscale behandeling van pensioen. Met de recente pensioentransitie naar het nieuwe stelsel is actuele kennis extra belangrijk.'
      },
      {
        question: 'Wat zijn de drie pijlers van het Nederlandse pensioenstelsel?',
        answer: 'Het Nederlandse pensioenstelsel bestaat uit drie pijlers. De eerste pijler is de AOW (Algemene Ouderdomswet), een basispensioen van de overheid voor iedereen die de AOW-leeftijd bereikt en in Nederland heeft gewoond. De tweede pijler is het werkgeverspensioen, opgebouwd via de werkgever bij een pensioenfonds of verzekeraar. Ongeveer 90% van de werknemers bouwt pensioen op in de tweede pijler. De derde pijler is individuele pensioenopbouw, zoals lijfrentes, banksparen of beleggen. Dit is vrijwillig en fiscaal gefaciliteerd voor mensen die geen of onvoldoende pensioen opbouwen in de tweede pijler, zoals zzp\'ers. Een goede pensioenadviseur helpt klanten om hun totale pensioeninkomen te overzien en eventuele tekorten aan te vullen.'
      },
      {
        question: 'Wat verandert er door de Wet toekomst pensioenen?',
        answer: 'De Wet toekomst pensioenen (Wtp), ingegaan in 2023, hervormt het Nederlandse pensioenstelsel fundamenteel. De belangrijkste veranderingen zijn: alle pensioenregelingen worden omgezet naar een beschikbare premieregeling (geen gegarandeerd eindresultaat meer), er komt een vlakke premie voor alle leeftijden in plaats van een stijgende premie, pensioenen kunnen eerder meebewegen met de economie (zowel omhoog als omlaag), en er komt meer persoonlijk pensioenvermogen. De transitie moet uiterlijk 1 januari 2028 zijn afgerond. Voor adviseurs betekent dit dat ze klanten moeten informeren over de gevolgen van de transitie, het invaren van bestaande rechten, en de nieuwe manier waarop pensioen wordt opgebouwd. Dit is een complex onderwerp dat veel vragen oproept bij klanten.'
      },
      {
        question: 'Wat gebeurt er met pensioen bij echtscheiding?',
        answer: 'Bij echtscheiding wordt het tijdens het huwelijk opgebouwde pensioen in principe verdeeld tussen beide partners (pensioenverevening). De Wet verevening pensioenrechten bij scheiding regelt dit. Het ouderdomspensioen wordt standaard 50/50 verdeeld, tenzij andere afspraken worden gemaakt in een huwelijkse voorwaarden of echtscheidingsconvenant. Het nabestaandenpensioen wordt omgezet in een eigen recht voor de ex-partner (bijzonder partnerpensioen). De verdeling moet binnen twee jaar na de scheiding worden gemeld bij de pensioenuitvoerder. Adviseurs moeten klanten wijzen op deze regels en helpen bij het maken van keuzes, bijvoorbeeld of ze willen afwijken van de standaardverdeling of het pensioen willen converteren naar een eigen aanspraak.'
      }
    ],
    
    meta: {
      title: 'WFT Pensioen Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je voor op het WFT Pensioen examen met AI-gegenereerde oefenvragen. Pensioenstelsels, regelingen en fiscaliteit. Officiële CDFD toetstermen. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-pensioen.webp'
    },
    
    isActive: true,
    hasQuestions: false,
    hasSummaries: false
  },

  'consumptief-krediet': {
    id: 'consumptief-krediet',
    slug: 'wft-consumptief-krediet',
    name: 'Consumptief Krediet',
    fullName: 'WFT Consumptief Krediet',
    shortDescription: 'Consumptief krediet: persoonlijke leningen, doorlopend krediet en kredietwaardigheid.',
    longDescription: 'Het WFT Consumptief Krediet examen toetst je kennis over consumptieve kredieten. Dit omvat persoonlijke leningen, doorlopend krediet, kredietvormen, kredietwaardigheid, BKR-registratie, en de Wet op het consumentenkrediet. Je leert klanten adviseren over verantwoord lenen.',
    
    specs: {
      totalQuestions: 31,
      duration: 90,
      passingScore: 68,
      passingPoints: 32,
      maxPoints: 47,
      kbQuestions: 15,
      kbPoints: 15,
      vcQuestions: 15,
      vcPoints: 30,
      pgQuestions: 1,
      pgPoints: 2
    },
    
    pricing: {
      examCost: 175,
      retakeCost: 175
    },
    
    topics: [
      'Wet op het consumentenkrediet (Wck)',
      'Persoonlijke lening',
      'Doorlopend krediet',
      'Kredietlimiet',
      'Effectieve jaarrente',
      'BKR (Bureau Krediet Registratie)',
      'Kredietwaardigheid',
      'Overkreditering',
      'NIBUD-normen',
      'Aflossingsvormen',
      'Vervroegd aflossen',
      'Betalingsachterstanden'
    ],
    
    keywords: [
      'wft consumptief krediet',
      'wft consumptief krediet examen',
      'wft consumptief krediet oefenvragen',
      'wft leningen',
      'kredietadviseur worden',
      'cdfd consumptief krediet'
    ],
    
    faqs: [
      {
        question: 'Hoeveel vragen heeft het WFT Consumptief Krediet examen?',
        answer: 'Het WFT Consumptief Krediet examen bestaat uit 31 vragen die je in 90 minuten (1,5 uur) moet beantwoorden. De verdeling is: 15 Kennis & Begrip (KB) vragen van elk 1 punt, 15 Vaardigheden & Competenties (VC) vragen van elk 2 punten, en 1 Professioneel Gedrag (PG) vraag van 2 punten. Het maximaal te behalen puntenaantal is 47. Om te slagen heb je minimaal 32 punten nodig (68%).'
      },
      {
        question: 'Wat leer ik bij WFT Consumptief Krediet?',
        answer: 'WFT Consumptief Krediet behandelt alle vormen van kredietverlening aan particulieren, uitgezonderd hypotheken. De hoofdonderwerpen zijn: persoonlijke leningen en doorlopende kredieten, creditcards en betaalkredieten, koop op afbetaling (huurkoop, financial lease), de Wet op het consumentenkrediet, kredietwaardigheidstoetsing en BKR-registratie, effectieve en nominale rentepercentages, kredietbescherming (overlijden, arbeidsongeschiktheid), schuldhulpverlening en de Wsnp, en verantwoorde kredietverlening. Je moet de maximale leencapaciteit kunnen berekenen op basis van de leennormen en klanten adviseren over de geschiktheid van verschillende kredietvormen.'
      },
      {
        question: 'Wat is het verschil tussen een persoonlijke lening en doorlopend krediet?',
        answer: 'Bij een persoonlijke lening leen je een vast bedrag dat je in vaste termijnen terugbetaalt over een afgesproken looptijd. De rente staat meestal vast en je weet precies wanneer de lening is afgelost. Bij een doorlopend krediet heb je een kredietlimiet waarvan je kunt opnemen wat je nodig hebt. Je betaalt alleen rente over het opgenomen bedrag. Als je aflost, komt die ruimte weer beschikbaar om opnieuw op te nemen. De rente is vaak variabel. Een persoonlijke lening is geschikt voor een eenmalige grote uitgave, doorlopend krediet voor flexibele financiële ruimte. De persoonlijke lening heeft meestal een lagere rente maar minder flexibiliteit.'
      },
      {
        question: 'Wat is de BKR en waarom is het belangrijk?',
        answer: 'De BKR (Bureau Krediet Registratie) is de stichting die in Nederland alle kredieten en betalingsachterstanden registreert. Elke lening vanaf €250 wordt gemeld, evenals creditcards, telefoonabonnementen met toestel, en koop-op-afbetaling. De BKR-registratie helpt kredietverstrekkers om te beoordelen of iemand een nieuw krediet aankan. Bij betalingsachterstanden krijg je een negatieve codering die tot 5 jaar na aflossing zichtbaar blijft. Dit kan het krijgen van nieuwe kredieten, een hypotheek of zelfs een huurwoning bemoeilijken. Adviseurs moeten klanten informeren over de gevolgen van kredietregistratie en hen helpen om overkreditering te voorkomen.'
      },
      {
        question: 'Hoe voorkom je overkreditering bij klanten?',
        answer: 'Overkreditering voorkom je door een grondige kredietwaardigheidstoets uit te voeren. Dit omvat: inkomenstoets (vast inkomen, variabele inkomsten), vermogenstoets (spaargeld, schulden), BKR-check (bestaande kredieten, negatieve coderingen), toetsing aan NIBUD-normen (hoeveel kan iemand verantwoord lenen), en een gesprek over de besteding en noodzaak van het krediet. De adviseur heeft een wettelijke zorgplicht om overkreditering te voorkomen. Dit betekent ook "nee" zeggen als een krediet niet verantwoord is, zelfs als de klant het graag wil. Bij signalen van financiële problemen moet je doorverwijzen naar schuldhulpverlening. Verantwoorde kredietverlening is een kerncompetentie van elke kredietadviseur.'
      }
    ],
    
    meta: {
      title: 'WFT Consumptief Krediet Oefenvragen & Examentraining | WFT Study Buddy',
      description: 'Bereid je voor op het WFT Consumptief Krediet examen met AI-gegenereerde oefenvragen. Leningen, kredietwaardigheid en BKR. Officiële CDFD toetstermen. Start gratis!',
      ogImage: 'https://wft-study-buddy.nl/og-consumptief-krediet.webp'
    },
    
    isActive: true,
    hasQuestions: false,
    hasSummaries: false
  }
};

// Helper functies
export const getExamBySlug = (slug: string): ExamConfig | undefined => {
  return Object.values(examConfigs).find(exam => exam.slug === slug);
};

export const getExamById = (id: string): ExamConfig | undefined => {
  return examConfigs[id];
};

export const getActiveExams = (): ExamConfig[] => {
  return Object.values(examConfigs).filter(exam => exam.isActive);
};

export const getExamsWithQuestions = (): ExamConfig[] => {
  return Object.values(examConfigs).filter(exam => exam.isActive && exam.hasQuestions);
};

export default examConfigs;